import styled from '@emotion/styled';
import { css } from '@emotion/react';
import tokens from '@vaad1st/shared.design-tokens/json/variables.json';

import { BreakPointToken } from '@vaad1st/hook.media-query';
import { NoGapSupport } from '../../lib/utils/mixins';

interface TickerPanelProps {
	hasImages: boolean;
}

interface DestinationPanelProps {
	timing: number;
	number: number;
	animate: boolean;
}

interface DestinationImageProps {
	timing: number;
	number: number;
}

interface TickerTextProps {
	timing: number;
	state: 'new' | 'old' | null;
	secondary: boolean;
}

const bounceTransition = 'cubic-bezier(.34,1.56,.64,1)';
const easeTransition = 'ease-in-out';

const getImageState = (
	number: number,
	animate: boolean
): { opacity: number; transformLtr: string; transformRtl: string } => {
	const transformLtr = animate
		? `translateX(${-1 * tokens.spacing_12px.value}px)`
		: 'translateX(0)';
	const transformRtl = animate
		? `translateX(${tokens.spacing_12px.value}px)`
		: 'translateX(0)';

	if (animate) {
		switch (number) {
			case 1:
				return {
					opacity: 0,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 2:
				return {
					opacity: 1,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 3:
				return {
					opacity: 0.68,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 4:
				return {
					opacity: 0.36,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			default:
				return {
					opacity: 0,
					transformLtr: 'none',
					transformRtl: 'none'
				};
		}
	} else {
		switch (number) {
			case 1:
				return {
					opacity: 1,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 2:
				return {
					opacity: 0.68,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 3:
				return {
					opacity: 0.36,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			case 4:
				return {
					opacity: 0,
					transformLtr: transformLtr,
					transformRtl: transformRtl
				};
			default:
				return {
					opacity: 0,
					transformLtr: 'none',
					transformRtl: 'none'
				};
		}
	}
};

const getTickerState = (
	state: 'new' | 'old' | null,
	secondary: boolean
): { opacity: number; transform: string } => {
	switch (state) {
		case 'new':
			return {
				opacity: 1,
				transform: 'translateY(0)'
			};
		case 'old':
			return {
				opacity: 0,
				transform: 'translateY(-100%)'
			};
		default:
			return {
				opacity: secondary ? 0 : 1,
				transform: secondary ? 'translateY(100%)' : 'translateY(0)'
			};
	}
};

export const TickerPanel = styled.div<TickerPanelProps>`
	padding: 0 ${tokens.spacing_12px.value}px;
	text-align: start;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	width: 100%;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		height: ${tokens.spacing_48px.value}px;
		padding: ${({ hasImages }) =>
			hasImages ? '0' : `0 ${tokens.spacing_12px.value}px`};
	}
`;

export const DestinationTeaser = styled.div`
	display: none;
	margin: ${-1 * tokens.spacing_2px.value}px 0;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		display: flex;
	}
`;

export const DestinationPanel = styled.div<DestinationPanelProps>`
	width: ${4 + tokens.spacing_48px.value}px;
	height: ${4 + tokens.spacing_48px.value}px;
	border: ${tokens.spacing_2px.value}px solid
		${tokens.color_light_background_neutral_primary};
	border-radius: ${tokens.spacing_48px.value}px;
	overflow: hidden;
	background: ${tokens.color_light_background_neutral_tertiary};
	margin-inline-start: ${-1 * tokens.spacing_40px.value}px;
	z-index: ${({ number }) => 4 - number};
	transition:
		transform ${({ timing }) => timing / 10}ms ${easeTransition},
		opacity ${({ timing }) => timing / 8}ms ${easeTransition};

	${({ number, animate }) => {
		const styles = getImageState(number, animate);
		return `
			opacity: ${styles.opacity};
			transform: ${styles.transformLtr};
			
			[dir="rtl"] & {
				transform: ${styles.transformRtl};
			}
		`;
	}}
	&:first-of-type {
		margin-inline-start: 0;
	}
`;

export const DestinationImage = styled.img<DestinationImageProps>`
	width: ${4 + tokens.spacing_48px.value}px;
	height: ${tokens.spacing_48px.value}px;
	opacity: 0;
	transition: opacity ${({ timing }) => timing / 8}ms ${easeTransition};
	transition-delay: ${({ number, timing }) => number * (timing / 18)}ms;
`;

export const TickerContainer = styled.div`
	flex-grow: 1;

	@media (min-width: ${BreakPointToken.LgMin}px) {
		display: flex;
		gap: ${tokens.spacing_8px.value}px;

		${NoGapSupport(
			() => css`
				& > *:first-of-type {
						margin-block: 0;
						margin-inline-end: ${tokens.spacing_8px.value}px;
						margin-inline-start: 0;
					}
				}
			`
		)}
	}
`;

export const TickerWrap = styled.span`
	overflow: hidden;
	position: relative;
	display: block;
	flex-grow: 1;
	height: ${tokens.font_body_xs_line_height_screen_xs.value}px;

	@media (min-width: ${BreakPointToken.LgMin}px) {
		height: ${tokens.font_body_m_line_height_screen_lg.value}px;
	}

	${NoGapSupport(
		() => css`
			margin-inline-start: 0.3em;
		`
	)}
`;

export const TickerText = styled.span<TickerTextProps>`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	display: inline-block;
	text-overflow: ellipsis;

	${({ state, secondary }) => {
		const styles = getTickerState(state, secondary);
		return `
			opacity: ${styles.opacity};
			transform: ${styles.transform};
		`;
	}}

	transition: transform ${({ timing }) => timing / 6}ms ${bounceTransition},
		opacity ${({ timing }) => timing / 4}ms ${bounceTransition};
`;
